import * as z from 'zod/v4';

import { invertObject, zodEnumFromObjectKeys } from '../utils/entry.js';

/**
 * Draw state, the state of a draw lifecycle
 */
export const DRAW_STATE_VARIANTS = {
  Undefined: 0,
  Defined: 1,
  Opened: 2,
  Regclosed: 3,
  Cancelclosed: 4,
  ResultEntered: 5,
  ResultVerified: 6,
  WincountStarted: 7,
  WincountFailed: 8,
  WincountDone: 9,
  WinvaluesEntered: 10,
  WinvaluesVerified: 11,
  WingenStarted: 12,
  WingenFailed: 13,
  WingenDone: 14,
  WinpayoutStarted: 15,
  WinpayoutFailed: 16,
  PublishStarted: 17,
  PublishFailed: 18,
  PublishDone: 19,
  Finalized: 20,
} as const;

/**
 * Zod enum for Draw state id, the state of a draw lifecycle
 */
export const drawStateIdEnum = z.enum(DRAW_STATE_VARIANTS);

/**
 * TS Draw state type, the state of a draw lifecycle
 */
export type DrawStateId = z.infer<typeof drawStateIdEnum>;

/**
 * Zod enum for Draw state, the state of a draw lifecycle
 */
export const drawStateEnum = zodEnumFromObjectKeys(DRAW_STATE_VARIANTS);

/**
 * Draw state id, the state of a draw lifecycle
 */
export const DRAW_STATE_ID_VARIANTS = invertObject<DrawState, DrawStateId>(DRAW_STATE_VARIANTS);

/**
 * TS Draw state type, the state of a draw lifecycle
 */
export type DrawState = z.infer<typeof drawStateEnum>;

/**
 * TS Draw state type, the state of a draw lifecycle
 *
 *    Unused:
 * NotDisabled: Normalfall
 * Regbet:  Spel (regbet/regwager) stoppas/startas – riktas mot spelmoduler i ITS
 * Cancelbet: Annulleringar (cancelbet/cancelwager) stoppas/startas. – riktas mot spelmoduler i ITS
 * Validation: Valideringar (validate_wager) stoppas startas – riktas mot ITS-VAL
 * AutoActions: Används inte längre. Användes för bingohallsspel.
 */
export const disableTypeEnum = z.enum(['Unused', 'NotDisabled', 'Regbet', 'Cancelbet', 'Validation', 'AutoActions']);
export type DisableType = z.infer<typeof disableTypeEnum>;

export const drawStateClientEnum = z.enum(['Defined', 'Opened', 'Live', 'Finished']);
// This is the state in the life cycle of a draw
export type DrawStateClient = z.infer<typeof drawStateClientEnum>;

// This is status of a draw, this can be combined with DrawStateClient, eg. it can be both opened and TemporarilyClosed
export type DrawStatusClient = 'TemporaryClosed' | 'Cancelled';

/*
 * - Defined - draw is defined but currently not playable
 * - Opened - draw is opened and playable as long as it doesn't is set to regBetDisabled: 'Disabled'
 * - Live - draw is live and will be updated with results, draw forecast exists
 * - Finished - draw is reported as finalized, no more results will be updated
 *
 **/
const getClientDrawState = (drawStateId: DrawStateId): DrawStateClient => {
  if (drawStateId < DRAW_STATE_VARIANTS.Opened) {
    return 'Defined';
  }

  if (drawStateId < DRAW_STATE_VARIANTS.Cancelclosed) {
    return 'Opened';
  }

  if (drawStateId < DRAW_STATE_VARIANTS.Finalized) {
    return 'Live';
  }

  return 'Finished';
};

export const drawStateSchema = drawStateIdEnum.transform((drawStateId) => {
  const drawStateClient = getClientDrawState(drawStateId);

  return {
    client: drawStateClient,
  } as const;
});
